import { motion } from 'motion/react'
import { Brain, Code, Heart, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Seo } from '~/components/Seo'
import { WebsiteLayout } from '~/lib/components/layouts/WebsiteLayout'
import { HeroSection } from '~/lib/components/website/HeroSection'
import { Section } from '~/lib/components/website/Section'
import { fadeInStagger } from '~/lib/utils/animations'

const values = [
  {
    icon: Brain,
    title: 'Innovation First',
    description:
      'We push boundaries and embrace cutting-edge technologies to solve complex problems.',
  },
  {
    icon: Users,
    title: 'Collaborative Spirit',
    description: 'We believe great ideas come from diverse perspectives and open discussions.',
  },
  {
    icon: Heart,
    title: 'User-Centric',
    description: "Everything we build starts with understanding our users' needs and challenges.",
  },
  {
    icon: Code,
    title: 'Technical Excellence',
    description: 'We maintain high standards in our code, architecture, and technical decisions.',
  },
]

const openPositions = [
  {
    title: 'Senior Full Stack Developer',
    department: 'Engineering',
    location: 'Remote (Europe)',
    type: 'Full-time',
  },
  {
    title: 'Machine Learning Engineer',
    department: 'AI & Research',
    location: 'Remote (Europe)',
    type: 'Full-time',
  },
  {
    title: 'Product Manager',
    department: 'Product',
    location: 'Remote (Europe)',
    type: 'Full-time',
  },
]

export default function Careers() {
  return (
    <>
      <Seo
        title="Careers | SOTA Navigator"
        description="Open roles at SOTA Navigator, the two-person team building literature review software for medical device clinical evaluation. Written applications welcome."
      />
      <WebsiteLayout
        showHero
        heroContent={
          <HeroSection
            title="Join Our Mission"
            subtitle="Help us revolutionize how researchers conduct literature reviews"
          />
        }
      >
        <Section title="Our Values" subtitle="What drives us forward and shapes our culture">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                variants={fadeInStagger(index * 0.1)}
                viewport={{ once: true }}
              >
                <Card className="h-full border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                        <value.icon className="size-10 text-[#001871]" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-[#001871]">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </Section>

        <Section
          dark
          title="Open Positions"
          subtitle="Join our team and help shape the future of research"
        >
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {openPositions.map((position, index) => (
              <motion.div
                key={position.title}
                variants={fadeInStagger(index * 0.1)}
                viewport={{ once: true }}
                className="rounded-xl border border-white/20 bg-white/10 p-6 backdrop-blur-xs"
              >
                <h3 className="mb-2 text-xl font-semibold text-white">{position.title}</h3>
                <div className="space-y-2 text-white/80">
                  <p>{position.department}</p>
                  <p>{position.location}</p>
                  <p>{position.type}</p>
                </div>
                <div className="mt-6">
                  <a
                    href={`mailto:careers@sotanavigator.com?subject=Application for ${position.title}`}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[#001871] transition-colors hover:bg-white/90"
                  >
                    Apply Now
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        <Section>
          <motion.div
            variants={fadeInStagger()}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl rounded-xl border border-[#001871]/10 bg-white p-8 text-center shadow-lg"
          >
            <h3 className="mb-6 text-2xl font-semibold text-[#001871]">Don't See a Perfect Fit?</h3>
            <p className="mb-8 text-gray-600">
              We're always looking for talented individuals who are passionate about improving
              research workflows. Send us your resume and let us know how you can contribute to our
              mission.
            </p>
            <div className="flex justify-center">
              <a
                href="mailto:careers@sotanavigator.com?subject=Open Application"
                className="inline-flex items-center gap-2 rounded-full bg-[#001871] px-6 py-3 text-white transition-colors hover:bg-[#001871]/90"
              >
                Send Open Application
              </a>
            </div>
          </motion.div>
        </Section>
      </WebsiteLayout>
    </>
  )
}
